/**
 * Utility functions for managing session data in localStorage and server-side
 */

import { patientApi } from '../services/api';
import { getTokensFromStorage } from './token';

// Constants for localStorage keys - CONSOLIDATED APPROACH
const VISIT_STATUS_KEY = 'ankr_visit_status'; // Unified session state
const FORM_DATA_PREFIX = 'ankr_form_data_';
const YOB_KEY_PREFIX = 'ankr_yob_'; // Key prefix for Year of Birth

/**
 * Unified session state structure:
 * {
 *   caseId: string,
 *   currentStep: number,
 *   visitStatus: 'pending'|'inProgress'|'completed'|'error',
 *   deviceCheck: {
 *     status: 'pending'|'success'|'failed'|'alternative',
 *     cameraStatus: 'pending'|'success'|'error'|'alternative',
 *     micStatus: 'pending'|'success'|'error'|'alternative',
 *     errorType: string, // 'Timeout'|'NotAllowedError'|'NotFoundError'|'Error'
 *     errorMessage: string,
 *     failureReasons: {
 *       noVideo: boolean,
 *       noMic: boolean,
 *       permissionDenied: boolean,
 *       deviceNotFound: boolean,
 *       timeout: boolean
 *     },
 *     deviceInfo: {
 *       userAgent: string,
 *       browser: string,
 *       device: string,
 *       ipAddress: string,
 *       timestamp: string
 *     },
 *     alternativeChoice: {
 *       choiceType: 'phone_visit'|'support'|null,
 *       phoneNumber: string,
 *       secondPhoneNumber: string,
 *       consentGiven: boolean,
 *       timestamp: string
 *     }
 *   },
 *   timestamp: string,
 *   lastActivity: string
 * }
 */

/**
 * Stores unified session state in localStorage
 * @param {Object} sessionData - The session data to store
 * @param {string} sessionData.caseId - The case ID
 * @param {number} sessionData.currentStep - Current step number
 * @param {string} sessionData.visitStatus - Visit status
 */
export const storeSessionState = async (sessionData) => {
  if (!sessionData.caseId) {
    return;
  }
  try {
    const unifiedState = {
      caseId: sessionData.caseId,
      currentStep: sessionData.currentStep || 1,
      visitStatus: sessionData.visitStatus || 'pending',
      timestamp: new Date().toISOString(),
      lastActivity: new Date().toISOString()
    };
    
    // Store locally first
    localStorage.setItem(VISIT_STATUS_KEY, JSON.stringify(unifiedState));
    
    // Also store on backend server
    try {
      const tokens = getTokensFromStorage();
      if (tokens && tokens.pin_jwt) {
        await patientApi.updatePatientStatus(sessionData.caseId, unifiedState, tokens);
      }
    } catch (backendError) {
      // Don't throw error - local storage still worked
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Retrieves unified session state from localStorage
 * @param {string} caseId - The case ID to validate against
 * @returns {Object|null} - The session state or null if not found or invalid
 */
export const getSessionState = (caseId) => {
  try {
    const sessionStateString = localStorage.getItem(VISIT_STATUS_KEY);
    if (!sessionStateString) return null;

    const sessionState = JSON.parse(sessionStateString);

    // Validate that the session belongs to the current case
    if (sessionState.caseId !== caseId) {
      console.warn('Stored session belongs to a different case, cleaning up old values');
      // Clean up old session data for different case
      clearSessionState();
      return null;
    }

    return sessionState;
  } catch (error) {
    console.error('Error retrieving session state:', error);
    return null;
  }
};

/**
 * Clears all session-related data from localStorage
 */
export const clearSessionState = () => {
  try {
    // Remove unified session state
    localStorage.removeItem(VISIT_STATUS_KEY);
    
    // Clean up legacy keys that might still exist
    localStorage.removeItem('ankr_current_step');
    
    // Clean up form data (keep only current case if caseId provided)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(FORM_DATA_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('Cleared session state and legacy data');
  } catch (error) {
    console.error('Error clearing session state:', error);
  }
};

/**
 * Legacy compatibility: Stores the current step number
 * @param {number} stepNumber - The current step number
 * @param {string} caseId - The case ID to associate with the step
 */
export const storeCurrentStep = async (stepNumber, caseId) => {
  const existingState = getSessionState(caseId) || {};
  await storeSessionState({
    ...existingState,
    caseId,
    currentStep: stepNumber
  });
};

/**
 * Legacy compatibility: Retrieves the stored step number
 * @param {string} caseId - The case ID to validate against
 * @returns {number|null} - The stored step number or null if not found or invalid
 */
export const getStoredStep = (caseId) => {
  const sessionState = getSessionState(caseId);
  return sessionState ? sessionState.currentStep : null;
};

/**
 * Stores the visit status in localStorage (now uses unified session state)
 * @param {string} status - The visit status ('pending', 'inProgress', 'completed', 'error')
 * @param {string} caseId - The case ID to associate with the status
 */
export const storeVisitStatus = async (status, caseId) => {
  const existingState = getSessionState(caseId) || {};
  await storeSessionState({
    ...existingState,
    caseId,
    visitStatus: status
  });
};

/**
 * Retrieves the stored visit status from localStorage
 * @param {string} caseId - The case ID to validate against
 * @returns {string|null} - The stored visit status or null if not found or invalid
 */
export const getStoredVisitStatus = (caseId) => {
  const sessionState = getSessionState(caseId);
  return sessionState ? sessionState.visitStatus : null;
};

/**
 * Enhanced session storage functions with server fallback for cross-browser support
 */

/**
 * Stores session state on server for cross-browser access
 * @param {string} caseId - The case ID
 * @param {Object} sessionData - The session data to store
 */
export const storeServerSessionState = async (caseId, sessionData) => {
  try {
    const response = await patientApi.updatePatientStatus(caseId, sessionData);
    if (response.data.success) {
      console.log('Session state stored on server successfully');
    }
  } catch (error) {
    console.error('Failed to store session state on server:', error);
  }
};

/**
 * Retrieves session state from server
 * @param {string} caseId - The case ID
 * @returns {Object|null} - The session state from server or null
 */
// Cache for server session state to avoid multiple API calls
let serverSessionCache = new Map();

export const getServerSessionState = async (caseId) => {
  // Check cache first
  if (serverSessionCache.has(caseId)) {
    return serverSessionCache.get(caseId);
  }
  
  try {
    // Get tokens for state parameter
    const tokens = getTokensFromStorage();
    const response = await patientApi.getPatientStatus(caseId, tokens);
    if (response.data.success) {
      // Extract the 'mira' session state from the nested structure
      const sessionState = response.data.sessionState;
      let result = null;
      if (sessionState && sessionState.mira) {
        result = sessionState.mira;
      } else {
        // Fallback to direct sessionState if not nested
        result = sessionState;
      }
      
      // Cache the result for 30 seconds to avoid redundant calls
      serverSessionCache.set(caseId, result);
      setTimeout(() => {
        serverSessionCache.delete(caseId);
      }, 30000);
      
      return result;
    }
  } catch (error) {
    console.error('Failed to retrieve session state from server:', error);
  }
  return null;
};

/**
 * Gets stored step with server as primary source for cross-browser support
 * @param {string} caseId - The case ID (MiraSol_CaseID UUID format)
 * @returns {number|null} - The stored step number
 */
export const getStoredStepWithServerFallback = async (caseId) => {
  // First try server for cross-browser persistence
  const serverState = await getServerSessionState(caseId);
  if (serverState && serverState.currentStep) {
    // Store locally for performance on subsequent calls
    storeSessionState(serverState);
    return serverState.currentStep;
  }
  
  // Fallback to localStorage if server doesn't have data
  return getStoredStep(caseId);
};

/**
 * Gets stored visit status with server as primary source for cross-browser support
 * @param {string} caseId - The case ID (MiraSol_CaseID UUID format)
 * @returns {string|null} - The stored visit status
 */
export const getStoredVisitStatusWithServerFallback = async (caseId) => {
  // First try server for cross-browser persistence
  const serverState = await getServerSessionState(caseId);
  if (serverState && serverState.visitStatus) {
    // Store locally for performance on subsequent calls
    storeSessionState(serverState);
    return serverState.visitStatus;
  }
  
  // Fallback to localStorage if server doesn't have data
  return getStoredVisitStatus(caseId);
};

/**
 * Stores form data in localStorage with a unique key
 * @param {string} formId - The form ID
 * @param {string} caseId - The case ID
 * @param {Object} formData - The form data to store
 */
export const storeFormData = (formId, caseId, formData) => {
  try {
    const key = `${FORM_DATA_PREFIX}${formId}_${caseId}`;
    const dataToStore = {
      formData,
      timestamp: new Date().toISOString(),
      caseId,
      formId
    };
    localStorage.setItem(key, JSON.stringify(dataToStore));
    console.log(`Stored form data for form ${formId}, case ${caseId}`);
  } catch (error) {
    console.error('Error storing form data:', error);
  }
};

/**
 * Retrieves form data from localStorage
 * @param {string} formId - The form ID
 * @param {string} caseId - The case ID
 * @returns {Object|null} - The stored form data or null if not found
 */
export const getStoredFormData = (formId, caseId) => {
  try {
    const key = `${FORM_DATA_PREFIX}${formId}_${caseId}`;
    const storedDataString = localStorage.getItem(key);
    if (!storedDataString) return null;

    const storedData = JSON.parse(storedDataString);
    
    // Validate case ID
    if (storedData.caseId !== caseId) {
      console.warn('Stored form data belongs to a different case, ignoring');
      return null;
    }

    return storedData.formData;
  } catch (error) {
    console.error('Error retrieving form data:', error);
    return null;
  }
};

/**
 * Stores Year of Birth in localStorage with case-specific key
 * @param {string} yob - The Year of Birth
 * @param {string} caseId - The case ID
 */
export const storeYearOfBirth = (yob, caseId) => {
  try {
    const key = `${YOB_KEY_PREFIX}${caseId}`;
    localStorage.setItem(key, yob);
  } catch (error) {
    console.error('Error storing Year of Birth:', error);
  }
};

/**
 * Retrieves Year of Birth from localStorage
 * @param {string} caseId - The case ID
 * @returns {string|null} - The stored Year of Birth or null if not found
 */
export const getStoredYearOfBirth = (caseId) => {
  try {
    const key = `${YOB_KEY_PREFIX}${caseId}`;
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

/**
 * Stores comprehensive device check information in unified session state
 * @param {Object} deviceCheckData - The device check data
 * @param {string} caseId - The case ID
 */
export const storeDeviceCheckInfo = (deviceCheckData, caseId) => {
  const existingState = getSessionState(caseId) || {};
  
  // Get browser and device information
  const userAgent = navigator.userAgent;
  const deviceInfo = {
    userAgent,
    browser: getBrowserInfo(userAgent),
    device: getDeviceInfo(userAgent),
    ipAddress: 'Unknown', // Will be populated by backend if needed
    timestamp: new Date().toISOString()
  };

  // Determine failure reasons based on device check data
  const failureReasons = {
    noVideo: deviceCheckData.cameraStatus === 'error',
    noMic: deviceCheckData.micStatus === 'error',
    permissionDenied: deviceCheckData.errorType === 'NotAllowedError',
    deviceNotFound: deviceCheckData.errorType === 'NotFoundError',
    timeout: deviceCheckData.errorType === 'Timeout'
  };

  const deviceCheck = {
    status: determineOverallStatus(deviceCheckData),
    cameraStatus: deviceCheckData.cameraStatus || 'pending',
    micStatus: deviceCheckData.micStatus || 'pending',
    errorType: deviceCheckData.errorType || '',
    errorMessage: deviceCheckData.errorMessage || '',
    failureReasons,
    deviceInfo,
    alternativeChoice: {
      choiceType: deviceCheckData.choiceType || null,
      phoneNumber: deviceCheckData.phoneNumber || '',
      secondPhoneNumber: deviceCheckData.secondPhoneNumber || '',
      consentGiven: deviceCheckData.consentGiven || false,
      timestamp: deviceCheckData.choiceType ? new Date().toISOString() : ''
    }
  };

  storeSessionState({
    ...existingState,
    caseId,
    deviceCheck
  });
};

/**
 * Retrieves device check information from unified session state
 * @param {string} caseId - The case ID
 * @returns {Object|null} - The device check data or null
 */
export const getStoredDeviceCheckInfo = (caseId) => {
  const sessionState = getSessionState(caseId);
  return sessionState ? sessionState.deviceCheck : null;
};

/**
 * Legacy compatibility: Stores device check error state
 * @param {Object} errorState - The error state data
 * @param {string} caseId - The case ID
 */
export const storeDeviceCheckError = (errorState, caseId) => {
  storeDeviceCheckInfo(errorState, caseId);
};

/**
 * Legacy compatibility: Retrieves device check error state
 * @param {string} caseId - The case ID
 * @returns {Object|boolean} - The device check data or false
 */
export const getStoredDeviceCheckError = (caseId) => {
  const deviceCheck = getStoredDeviceCheckInfo(caseId);
  return deviceCheck || false;
};

// Helper functions
function getBrowserInfo(userAgent) {
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Unknown';
}

function getDeviceInfo(userAgent) {
  if (/Mobile|Android|iPhone|iPad/.test(userAgent)) return 'Mobile';
  if (/Tablet|iPad/.test(userAgent)) return 'Tablet';
  return 'Desktop';
}

function determineOverallStatus(deviceCheckData) {
  if (deviceCheckData.choiceType) return 'alternative';
  if (deviceCheckData.cameraStatus === 'success' && deviceCheckData.micStatus === 'success') return 'success';
  if (deviceCheckData.cameraStatus === 'error' || deviceCheckData.micStatus === 'error') return 'failed';
  return 'pending';
}

/**
 * Clears all stored data for a specific case
 * @param {string} caseId - The case ID to clear data for
 */
export const clearCaseData = (caseId) => {
  try {
    // Clear unified session state if it belongs to this case
    const sessionState = getSessionState(caseId);
    if (sessionState) {
      clearSessionState();
    }
    
    // Clear form data for this case
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(FORM_DATA_PREFIX) && key.includes(caseId)) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear YOB for this case
    const yobKey = `${YOB_KEY_PREFIX}${caseId}`;
    localStorage.removeItem(yobKey);
    
    // Device check data is now part of unified session state, so it's already cleared above
    console.log(`Cleared all data for case: ${caseId}`);
  } catch (error) {
    console.error('Error clearing case data:', error);
  }
};